// The synergy browser — every tribe in the game, always, whether or not
// you're building it.
//
// This is the reference view the product never had. Before this, a player
// could only learn what La Clica or Bajitos do by accidentally drafting into
// them: the tavern chip row hid any tribe below 2 units and showed only the
// rung already reached, so both "what exists" and "what am I building
// toward" were invisible.
//
// Shell is deliberately the same as ScoutingPanel — right slide-in, backdrop
// dismiss, Escape, role="dialog". The tavern already taught this gesture for
// Scout; a second drawer should not invent a second vocabulary.

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Card } from '../game/data/cards/types';
import { TRIBE_DEFINITIONS } from '../game/data/tribes';
import { allTribeProgress, orderTribesForRow } from './synergyProgress';
import { TribeDetail } from './TribeDetail';
import { useEffectiveReducedMotion } from './useReducedMotion';
import { EDGES } from '../theme/tokens';

interface SynergyPanelProps {
  open: boolean;
  onClose: () => void;
  board: (Card | null)[];
  hand: Card[];
  shop: Card[];
}

export function SynergyPanel({ open, onClose, board, hand, shop }: SynergyPanelProps) {
  const reducedMotion = useEffectiveReducedMotion();

  // Escape closes. ScoutingPanel doesn't do this — it should, but fixing it
  // there is a separate change.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const progress = orderTribesForRow(allTribeProgress(board, hand, shop));
  const activeCount = progress.filter((p) => p.active !== null).length;

  // Fielded units per tribe, so each card can name what's actually counting.
  const unitsByTribe = new Map<string, Card[]>();
  for (const slot of board) {
    if (!slot?.tribe) continue;
    const list = unitsByTribe.get(slot.tribe) ?? [];
    list.push(slot);
    unitsByTribe.set(slot.tribe, list);
  }

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-asphalt/70 backdrop-blur-sm"
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            key="synergy-drawer"
            initial={reducedMotion ? { opacity: 0 } : { x: '100%' }}
            animate={reducedMotion ? { opacity: 1 } : { x: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { x: '100%' }}
            transition={{ duration: reducedMotion ? 0 : 0.22, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="pt-safe pb-safe flex h-full w-full max-w-md flex-col gap-sm border-l bg-asphalt p-md md:gap-md md:p-lg"
            style={{ borderColor: EDGES.mid }}
            role="dialog"
            aria-modal="true"
            aria-label="Tribe synergies"
          >
            <div className="flex items-baseline justify-between gap-sm">
              <div className="flex min-w-0 flex-col">
                <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
                  synergies
                </p>
                <h2 className="font-cholo text-[22px] leading-none text-bone md:text-[28px]">
                  {activeCount === 0
                    ? 'None active yet'
                    : `${activeCount} active`}
                </h2>
              </div>
              <button
                onClick={onClose}
                aria-label="close synergies"
                className="shrink-0 font-data text-[11px] uppercase tracking-[0.2em] text-graphite hover:text-marker md:text-[12px]"
              >
                close ×
              </button>
            </div>

            <p className="font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
              field 2, 4 or 6 of a tribe. only board units count — hand and
              shop are shown as potential.
            </p>

            <div className="flex flex-1 flex-col gap-xs overflow-y-auto pr-xs md:gap-sm">
              {progress.map((p) => (
                <TribeDetail
                  key={p.tribe.id}
                  progress={p}
                  units={unitsByTribe.get(p.tribe.id) ?? []}
                />
              ))}
            </div>

            <p className="shrink-0 font-data text-[9px] uppercase tracking-[0.2em] text-graphite md:text-[10px]">
              {TRIBE_DEFINITIONS.length} tribes · 3 per faction
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
